/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import {
  call,
  all,
  put,
  delay,
  select,
  race,
  take,
  getContext,
} from "typed-redux-saga";
import { PayloadAction } from "typesafe-actions";
import { v4 as uuidv4 } from "uuid";
import { data } from "./dataSaga";
import { RootState, rootActions } from "..";
import { getGlobalConfigs } from "@/index";
import { toUTCISOString } from "../../modules/dateFormats/index";
import {
  selectStartAndEnd,
  selectIsRollingMode,
  selectUsername,
  selectEnableIPC,
  selectMachines,
} from "@/store/selectors";
import { CMSClientType } from "@/cms-api";
import {
  Event,
  OffsetAdjustmentAppliedAndCalculation,
} from "@centralwebteam/narwhal";
import { ProcessUpdatePresentation } from "@/presentation/processUpdates";
import { selectLastFetchedForRefresh } from "./selectors";
import { computeAllGroupedTableData } from "./utils";
import { ProcessUpdatesGroupedData } from "../state";
import { notificationTypes, uniqueID } from "@centralwebteam/jellyfish";
import i18n from "@/i18n";
import type { TFunction } from "i18next";
import { actions as globalActions } from "@/store/global/actions";
import constants from "@/constants";

type OffsetAdjustmentRequest = {
  processUpdate: ProcessUpdatePresentation;
  key: string;
  requestId: string;
};

type SetOffsetValuesResult = {
  status: "Succeeded" | "Failed";
  offsetTarget?: { id?: string };
  errorDetails?: {
    message?: string;
    errorName?: string;
    advisedAction?: string;
  };
};

type SetOffsetValuesResponse = Event & {
  type: "ServiceResponse";
  subType: "SetOffsetValuesResponse";
  requestId: string;
  details?: {
    responses?: SetOffsetValuesResult[];
    responseStatus?: { status: "Succeeded" | "Failed" };
  };
};

/**
 * Fetches all of the required static data for the current time range.
 */
export const allStaticData = function* () {
  try {
    yield* all([call(fetchStaticData)]);
  } catch (error) {
    console.log(error);
  }
};

export function* liveRefreshWatcher() {
  const config = yield* call(() => getGlobalConfigs());
  while (true) {
    // Loop every 1 second
    yield* delay(1000);

    const isRollingMode = yield* select(selectIsRollingMode);
    if (!isRollingMode) {
      continue;
    }

    const isLiveRefreshModeOn = yield* select(
      (state: RootState) => state.global.isLiveRefreshModeOn
    );
    if (!isLiveRefreshModeOn) {
      continue;
    }

    // Check if current date is after last fetched date + refresh rate
    const timeSinceLastFetchCompleted = yield* select(
      selectLastFetchedForRefresh
    );
    if (timeSinceLastFetchCompleted) {
      const now = new Date();
      const timeSinceLastFetch =
        now.getTime() - timeSinceLastFetchCompleted.getTime();

      if (timeSinceLastFetch < config.refreshRateFastInMs) {
        continue;
      }
    }
    yield* race({
      fetchDelta: call(fetchDeltas, config.maxTake),
      cancel: call(waitForLiveRefreshModeChanged),
    });
  }
}

function* waitForLiveRefreshModeChanged() {
  do {
    const isLiveRefreshModeOn = yield* select(
      (state: RootState) => state.global.isLiveRefreshModeOn
    );
    if (!isLiveRefreshModeOn) {
      return;
    }
  } while (yield* take(rootActions.global.liveRefreshModeStateChanged));
}

function* fetchStaticData() {
  const config = yield* call(() => getGlobalConfigs());
  const [from, to] = yield* select(selectStartAndEnd);
  try {
    yield* put(rootActions.processUpdates.fetchingStarted());
    yield* call(fetchOffsetAdjustment, from, to, config.maxTake, "static");
  } catch (error) {
    console.info(
      "something went wrong fetching static process updates data",
      error
    );
  } finally {
    yield* put(
      rootActions.processUpdates.fetchingFinished({
        to,
      })
    );
  }
}

function* fetchDeltas(take: number) {
  const now = toUTCISOString(new Date());
  try {
    const lastFetched = yield* select(
      (state: RootState) => state.processUpdates.lastFetched
    );
    const isRollingMode = yield* select(selectIsRollingMode);
    const isLiveRefreshModeOn = yield* select(
      (state) => state.global.isLiveRefreshModeOn
    );

    // comply with types
    if (!lastFetched || !isRollingMode || !isLiveRefreshModeOn) {
      return;
    }
    yield* put(rootActions.processUpdates.fetchingStarted());
    yield* call(fetchOffsetAdjustment, lastFetched, now, take, "delta");
  } catch (error) {
    console.log(error);
  } finally {
    yield* put(
      rootActions.processUpdates.fetchingFinished({
        to: now,
      })
    );
  }
}

function* fetchOffsetAdjustment(
  from: string,
  to: string,
  take: number,
  type: "static" | "delta"
) {
  try {
    const offsetAdjustments = yield* call(
      (from, to, take) =>
        data.fetchOffsetAdjustment({
          from,
          to,
          take,
        }),
      from,
      to,
      take
    );
    const appliedOffsetEvents = yield* select(
      (state: RootState) => state.processUpdates.appliedOffsetEvents
    );
    const reconciledAdjustments = offsetAdjustments.map((event) =>
      event.subType === "OffsetAdjustmentCalculation"
        ? (appliedOffsetEvents[event.id] ?? event)
        : event
    );
    const payload = {
      events: reconciledAdjustments,
      groupedData: {} as ProcessUpdatesGroupedData,
      from: from,
      to: to,
    };
    if (type === "static") {
      const groupedData = yield* call(
        computeAllGroupedTableData,
        reconciledAdjustments
      );
      payload.groupedData = groupedData;

      yield* put(rootActions.processUpdates.processActionsFetched(payload));
    } else {
      const currentProcessActions = yield* select(
        (state: RootState) => state.processUpdates.processActions
      );
      const combinedProcessActions = [
        ...new Map(
          [...currentProcessActions, ...reconciledAdjustments].map((event) => [
            event.id,
            event,
          ])
        ).values(),
      ];
      const groupedData = yield* call(
        computeAllGroupedTableData,
        combinedProcessActions
      );
      payload.groupedData = groupedData;
      payload.events = combinedProcessActions;

      yield* all([
        put(rootActions.processUpdates.processActionsDeltaFetched(payload)),
      ]);
    }
  } catch (error) {
    console.log(error);
  }
}

export function* getJobDetailsByIdSaga({
  payload: { processUpdate },
}: PayloadAction<
  "[process updates] details table inner table row clicked",
  { processUpdate: ProcessUpdatePresentation & { jobId: string } }
>) {
  const client: CMSClientType = yield getContext("client");
  const id = processUpdate.jobId;
  try {
    const job = yield* call(() => client.jobs.byId(id).promise);
    if (job)
      yield* put(
        rootActions.processUpdates.detailsTableInnerTableRowClickedRedirect({
          job,
          processUpdate,
        })
      );
    // load related jobs of focused job after redirect
    yield* put(
      rootActions.processUpdates.detailsTableInnerTableRowClickedSetJobDetails({
        job,
      })
    );
  } catch (e) {
    console.log("job not found");
  }
}

/**
 * Builds the `SetOffsetValuesRequest` service request for every offset update
 * targeting a single machine. The API routes this onto MQTT and the IPC
 * dispatcher posts back applied/failed events carrying the same requestId.
 */
function buildSetOffsetValuesRequestPayload(
  offsets: OffsetAdjustmentRequest[],
  requestId: string,
  operatorId: string | undefined
) {
  const sourceEvent = offsets[0].processUpdate.rawEvent;
  const details = sourceEvent.details;

  return [
    {
      created: new Date().toISOString(),
      type: "ServiceRequest",
      subType: "SetOffsetValuesRequest",
      source: constants.setOffsetValuesRequest.source,
      payloadVersion: constants.setOffsetValuesRequest.payloadVersion,
      settingsId: sourceEvent.settingsId,
      correlationId: sourceEvent.correlationId,
      dispatchMethod:
        sourceEvent.dispatchMethod ??
        details?.executionSettings?.dispatchMethod ??
        constants.setOffsetValuesRequest.defaultDispatchMethod,
      requestId,
      operatorId,
      details: {
        processTarget: details?.processTarget,
        requests: offsets.map(({ processUpdate }) => {
          const action = processUpdate.rawEvent.details?.action;
          return {
            offsetTarget: processUpdate.rawEvent.details?.offsetTarget,
            action: {
              type: "OffsetUpdate",
              updateMode: action?.updateMode,
              units: action?.units,
              cncProposedNativeValue: action?.cncProposedNativeValue,
              cncProposedValue: action?.cncProposedValue,
            },
          };
        }),
        executionSettings: {
          runMode: "Auto",
          updateMode: "Control",
          retriesAndTimeOuts: details?.executionSettings?.retriesAndTimeOuts,
        },
      },
    },
  ];
}

/** A single service request is sent per target machine, batching its offsets. */
const groupOffsetsByTargetMachine = (offsets: OffsetAdjustmentRequest[]) => {
  const groups = new Map<string, OffsetAdjustmentRequest[]>();
  for (const offset of offsets) {
    const machineId = offset.processUpdate.targetMachine?.id;
    if (!machineId) {
      throw new Error("Target machine id missing");
    }
    const group = groups.get(machineId);
    if (group) {
      group.push(offset);
    } else {
      groups.set(machineId, [offset]);
    }
  }
  return [...groups.values()];
};

function* applySetOffsetValues(offsets: OffsetAdjustmentRequest[]) {
  const client = yield* getContext<CMSClientType>("client");
  const requestId = uuidv4();

  try {
    const enableIPC = yield* select(selectEnableIPC);
    if (!enableIPC) {
      throw new Error("No active IPC service found");
    }

    const allMachines = yield* select(selectMachines);
    const ipcServices = allMachines
      .filter(
        (m) => m.type === "Service" && m.model === "IPC Service" && m.isLicensed
      )
      .sort((a, b) =>
        (a.lastContact ?? Number.MAX_SAFE_INTEGER) <
        (b.lastContact ?? Number.MAX_SAFE_INTEGER)
          ? 1
          : -1
      );

    if (!ipcServices.length) {
      throw new Error("No active IPC service found");
    }

    const ipcServiceMachineId = ipcServices[0].id;
    const operatorId = yield* select(selectUsername);
    const payload = buildSetOffsetValuesRequestPayload(
      offsets,
      requestId,
      operatorId
    );

    yield* call(() => client.events.add(ipcServiceMachineId, payload).promise);

    const interval = 3 * 1000; // 3 seconds in milliseconds
    const timeout = 60 * 1000; // 30 seconds in milliseconds

    const response = yield* call(
      pollForSetOffsetValuesResponse,
      client,
      requestId,
      interval,
      timeout
    );

    let appliedCount = 0;
    const t = yield* call(() => i18n);
    for (const offset of offsets) {
      const sourceEvent = offset.processUpdate.rawEvent;
      const offsetTargetId = sourceEvent.details?.offsetTarget?.id;
      const result = response?.details?.responses?.find(
        (r) => r.offsetTarget?.id === offsetTargetId
      );

      if (response && result?.status === "Succeeded") {
        appliedCount++;
        // OffsetValuesResponse only reports per-offset status, so the applied row
        // is derived from the calculation event that was sent for applying.
        const appliedEvent = {
          ...sourceEvent,
          subType: "OffsetAdjustmentApplied",
          created: response.created,
          requestId: offset.requestId,
        } as OffsetAdjustmentAppliedAndCalculation;
        yield* put(
          rootActions.processUpdates.offsetAdjustmentApplied({
            key: offset.key,
            requestId: offset.requestId,
            event: appliedEvent,
            update: new ProcessUpdatePresentation(appliedEvent),
          })
        );
      } else {
        yield* put(
          rootActions.processUpdates.applyOffsetAdjustmentFailed({
            key: offset.key,
            error: getSetOffsetValuesError(t, response, result),
          })
        );
      }
    }

    return appliedCount;
  } catch (error) {
    console.error("Failed to apply offset adjustments:", error);
    for (const offset of offsets) {
      yield* put(
        rootActions.processUpdates.applyOffsetAdjustmentFailed({
          key: offset.key,
          error: error instanceof Error ? error.message : "Unknown error",
        })
      );
    }
    return 0;
  }
}

export function* applyOffsetAdjustmentsSaga({
  payload: { offsets },
}: PayloadAction<
  "[process updates] apply offset adjustments",
  { offsets: OffsetAdjustmentRequest[] }
>) {
  let outcomes: number[] = [];
  try {
    const groups = groupOffsetsByTargetMachine(offsets);
    outcomes = yield* all(
      groups.map((group) => call(applySetOffsetValues, group))
    );
  } catch (error) {
    console.error("Failed to apply offset adjustments:", error);
    for (const offset of offsets) {
      yield* put(
        rootActions.processUpdates.applyOffsetAdjustmentFailed({
          key: offset.key,
          error: error instanceof Error ? error.message : "Unknown error",
        })
      );
    }
  }
  const appliedCount = outcomes.reduce((total, count) => total + count, 0);
  const failedCount = offsets.length - appliedCount;
  const t = yield* call(() => i18n);
  const config = yield* call(() => getGlobalConfigs());
  const id = uniqueID();
  const type =
    failedCount === 0 ? notificationTypes.success : notificationTypes.error;

  const header =
    failedCount === 0
      ? t("heading-OffsetAdjustmentsApplied")
      : appliedCount === 0
        ? t("heading-OffsetAdjustmentsFailed")
        : t("heading-OffsetAdjustmentsPartiallyApplied");
  const body = [
    ...(appliedCount > 0
      ? [t("message-OffsetAdjustmentsAppliedCount", { count: appliedCount })]
      : []),
    ...(failedCount > 0
      ? [t("message-OffsetAdjustmentsFailedCount", { count: failedCount })]
      : []),
  ];

  yield* put(
    globalActions.setNotification({
      show: true,
      header,
      body,
      id,
      type,
    })
  );
  yield* delay(config.notificationTimeoutInMS);
  const notification = yield* select(
    (state: RootState) => state.global.notification
  );
  if (notification.show && notification.id === id) {
    yield* put(
      globalActions.setNotification({
        show: false,
        header: "",
        body: [],
        id: "",
        type,
      })
    );
  }
}

function getSetOffsetValuesError(
  t: TFunction,
  response: SetOffsetValuesResponse | undefined,
  result: SetOffsetValuesResult | undefined
) {
  if (!response) {
    return t("message-OffsetAdjustmentTimedOut");
  }
  if (!result) {
    return t("message-OffsetAdjustmentNoResult");
  }
  return (
    result.errorDetails?.message ??
    result.errorDetails?.errorName ??
    t("message-OffsetAdjustmentNotApplied")
  );
}

/**
 * Polls the events API until the IPC dispatcher has posted back the
 * SetOffsetValuesResponse for the request, or the timeout elapses.
 * Returns undefined on timeout.
 */
function* pollForSetOffsetValuesResponse(
  client: CMSClientType,
  requestId: string,
  pollIntervalInMs: number,
  pollTimeoutInMs: number
) {
  const start = Date.now();

  while (Date.now() - start < pollTimeoutInMs) {
    yield* delay(pollIntervalInMs);

    const response = yield* call(
      () =>
        client.events.query<SetOffsetValuesResponse[]>(
          `$filter=type eq ServiceResponse and subtype eq SetOffsetValuesResponse and requestId eq ${requestId}&$orderby created desc&$top=1`
        ).promise
    );

    if (Array.isArray(response) && response.length > 0) {
      return response[0];
    }
  }

  return undefined;
}
